class TagHelper
{

    constructor (){}

    static extractTagNamesFromObjects(videoTagList)
    {
        if (!Array.isArray(videoTagList)) return [];
        
        return videoTagList.map((tagDetails) => {
            // Handle different tag structures
            if (typeof tagDetails === 'string') {
                return tagDetails; // Direct string tag
            } else if (tagDetails?.tagName) {
                return tagDetails.tagName; // Object with tagName property
            } else if (tagDetails?.name) {
                return tagDetails.name; // Object with name property
            } else if (tagDetails?.tag) {
                return tagDetails.tag; // Object with tag property
            } else if (tagDetails?.value) {
                return tagDetails.value; // Object with value property
            } else if (tagDetails?.text) {
                return tagDetails.text; // Object with text property
            } else if (tagDetails?.label) {
                return tagDetails.label; // Object with label property
            } else {
                // Try to convert to string if it's a simple object
                return typeof tagDetails === 'object' ? JSON.stringify(tagDetails) : String(tagDetails);
            }
        }).filter(Boolean);
    }

    static mergeTagLists(primary = [], secondary = []) {
        const mergedMap = new Map();
        const addTag = (tag) => {
            if (tag === undefined || tag === null) {
                return;
            }
            const identity = this.getTagIdentity(tag);
            if (!identity) {
                return;
            }
            if (!mergedMap.has(identity)) {
                mergedMap.set(identity, tag);
                return;
            }
            const existingTag = mergedMap.get(identity);
            if (this.shouldReplaceTag(existingTag, tag)) {
                mergedMap.set(identity, tag);
            }
        };
        primary.forEach(addTag);
        secondary.forEach(addTag);
        return Array.from(mergedMap.values());
    }

    static persistTags(postId, keyPath, tags = []) {
        if (typeof window === 'undefined') {
            return;
        }
        const normalizedTags = Array.isArray(tags) ? tags : [];
        const storageKeys = this.buildStorageKeys(postId, keyPath);
        storageKeys.forEach((storageKey) => {
            try {
                localStorage.setItem(storageKey, JSON.stringify(normalizedTags));
            } catch (error) {
                console.warn('Failed to persist tags', storageKey, error);
            }
        });
    }

    static getPersistedTags(postId, keyPath) {
        if (typeof window === 'undefined') {
            return [];
        }
        const storageKeys = this.buildStorageKeys(postId, keyPath);
        for (const storageKey of storageKeys) {
            try {
                const raw = localStorage.getItem(storageKey);
                if (!raw) {
                    continue;
                }
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    return parsed;
                }
            } catch (error) {
                console.warn('Failed to read persisted tags', storageKey, error);
            }
        }
        return [];
    }

    static buildStorageKeys(postId, keyPath) {
        const storageKeys = new Set();
        if (postId) {
            storageKeys.add(`uploadedVideoTags_${postId}`);
        }
        const keyVariants = this.getKeyPathVariants(keyPath);
        keyVariants.forEach((variant) => {
            storageKeys.add(`uploadedVideoTags_key_${variant}`);
        });
        return Array.from(storageKeys);
    }

    static getKeyPathVariants(keyPath) {
        if (!keyPath || typeof keyPath !== 'string') {
            return [];
        }
        const addVariant = (set, value) => {
            if (!value) {
                return;
            }
            const trimmedValue = value.trim();
            if (trimmedValue) {
                set.add(trimmedValue);
            }
        };
        const variants = new Set();
        const trimmed = keyPath.trim();
        if (!trimmed) {
            return [];
        }
        const withoutHash = trimmed.split('#')[0];
        const withoutQuery = withoutHash.split('?')[0];
        const withoutProtocol = withoutQuery.replace(/^[a-z]+:\/\/[\w.-]+(?=\/|$)/i, '');
        const withoutProtocolOrLeadingSlash = withoutProtocol.replace(/^\/+/, '');
        addVariant(variants, withoutProtocolOrLeadingSlash);
        addVariant(variants, withoutProtocol);
        addVariant(variants, withoutQuery);
        addVariant(variants, withoutHash);
        addVariant(variants, trimmed);
        try {
            const url = new URL(trimmed);
            const pathname = url.pathname || '';
            addVariant(variants, pathname.replace(/^\/+/, ''));
            addVariant(variants, pathname);
        } catch {}
        return Array.from(variants);
    }

    static getTagIdentity(tag) {
        if (typeof tag === 'string') {
            if (this.isLikelyObjectId(tag)) {
                return `id:${tag.toLowerCase()}`;
            }
            return `string:${tag}`;
        }
        if (tag?._id) {
            return `id:${String(tag._id).toLowerCase()}`;
        }
        return tag?.tagName
            || tag?.name
            || tag?.tag
            || tag?.value
            || tag?.text
            || tag?.label
            || JSON.stringify(tag);
    }

    static isLikelyObjectId(value) {
        if (typeof value !== 'string') {
            return false;
        }
        return /^[a-f0-9]{24}$/i.test(value.trim());
    }

    static shouldReplaceTag(existingTag, newTag) {
        return this.getTagDetailScore(newTag) > this.getTagDetailScore(existingTag);
    }

    static getTagDetailScore(tag) {
        if (tag === undefined || tag === null) {
            return 0;
        }
        if (typeof tag === 'string') {
            return this.isLikelyObjectId(tag) ? 1 : 4;
        }
        if (typeof tag !== 'object') {
            return 1;
        }
        let score = 1;
        if (tag._id) {
            score += 1;
        }
        if (tag.tagName || tag.name || tag.tag || tag.value || tag.text || tag.label) {
            score += 4;
        }
        return score;
    }

    // Debug function to show tag structure
    static debugTagStructure(videoTagList) {
        console.log("🏷️ Tag Structure Debug:");
        console.log("Raw videoTagList:", videoTagList);
        
        if (Array.isArray(videoTagList)) {
            videoTagList.forEach((tag, index) => {
                console.log(`Tag ${index}:`, {
                    type: typeof tag,
                    value: tag,
                    tagName: tag?.tagName,
                    name: tag?.name,
                    tag: tag?.tag,
                    value: tag?.value,
                    text: tag?.text,
                    label: tag?.label
                });
            });
        }
        
        const extracted = this.extractTagNamesFromObjects(videoTagList);
        console.log("Extracted tags:", extracted);
        return extracted;
    }

}

export default TagHelper;